import * as React from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import StringResourceManager, { GetString } from '../StringResourceManager';
import { Dialog } from '@progress/kendo-react-dialogs';
import { useDispatch, useSelector } from 'react-redux';
import { createStyles, makeStyles } from '@material-ui/core';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridCustomFilterCellProps,
} from '@progress/kendo-react-grid';
import { DataResult, State, process } from '@progress/kendo-data-query';
import {
  setExpandedState,
  setGroupIds,
} from '@progress/kendo-react-data-tools';
import { ApplicationState } from '../../store';
import {
  getLocalDateTime,
  getScheduledCommandStatusToEnum,
  scheduledCommandStatusIconMap,
  useChipStyleScheduledCommand,
} from '../../utils/utils';
import { CustomDateFilterCell } from '../CustomDateFilterCell';
import { Chip } from '@progress/kendo-react-buttons';
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { scheduledCommandStatusTextMap } from '../../utils/textMappings';
import { LoadingPanel } from '../LoadingPanel';
import { Label } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import { Divider } from '@mui/material';
import {
  getScheduledCommandByBatchID,
  resetNotificationMessage,
  updateScheduledCommandByBatchId,
  updateScheduledCommandById,
} from '../../store/Commands/CommandsAction';
import clsx from 'clsx';
import { ScheduledCommandHistoryRecord } from '../../Interfaces/Commands/ScheduledCommandRecordInfo';
import cronstrue from 'cronstrue';
import 'cronstrue/locales/fr';
import { LocaleInterface, locales } from '../AlertsGrid/GridLocale';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { COMMAND_NAME } from '../../utils/constants';
import { ScheduledStatusDropDownFilterCell } from './ScheduleDropdownFiltercell';

const useStyles = makeStyles(theme =>
  createStyles({
    cancelButton: {
      width: '180px',
      border: '1px solid #000000',
    },
    buttonStyle: {
      alignItems: 'flex-end',
      marginTop: 33,
    },
    modalContainer: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '80vw',
    },
    dialogTitle: {
      display: 'flex',
      flexDirection: 'row',
    },
    selectedLanesView: {
      marginTop: 5,
      marginBottom: 5,
    },
    titleDate: {
      marginLeft: 10,
    },
    doneButtonView: {
      marginTop: 10,
      textAlign: 'center',
    },
    scheduleUploadLogsContainer: {
      margin: 2,
    },
    lineChartHeader: {
      fontSize: 20,
      textDecorationLine: 'underline',
      marginBottom: 10,
      fontWeight: 'bold',
    },
    queryLabel: {
      display: 'flex',
      flexDirection: 'row',
      fontSize: 18,
    },
    queryInput: {
      fontWeight: 'bold',
      fontSize: 18,
      paddingLeft: 5,
    },
    wrapInput: {
      display: 'block',
      maxWidth: '90%',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
    },
  })
);

const CreatedOnCell = (props: GridCellProps) => {
  const dataItem = props.dataItem;
  return <td>{getLocalDateTime(dataItem.CreatedOn)}</td>;
};

interface Props {
  handleCloseDialog: (open: boolean) => void;
  command: string;
  title: string;
}

const ScheduledCommandHistoryDialog: React.FC<Props> = ({
  handleCloseDialog,
  command,
  title,
}): React.ReactElement => {
  const classes = useStyles();

  const initialDataState: State = {
    take: 50,
    skip: 0,
    group: [],
  };

  const dispatch = useDispatch();

  const [dataState, setDataState] = React.useState<State>(initialDataState);

  const [collapsedState, setCollapsedState] = React.useState<string[]>([]);

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0]
  );

  const scheduledCommandList: ScheduledCommandHistoryRecord[] = useSelector(
    (state: ApplicationState) => state.commands.scheduledCommandListByBatchID
  );

  const loading: boolean = useSelector(
    (state: ApplicationState) => state.commands.isLoadingCommands
  );

  const message: string = useSelector(
    (state: ApplicationState) => state.commands.notificationMessage
  );

  const [creationDate, setCreationDate] = React.useState<string>('');
  const [startDate, setStartDate] = React.useState<string | null>('');
  const [endDate, setEndDate] = React.useState<string | null>('');
  const [frequency, setFrequency] = React.useState<string>('');
  const [files, setFiles] = React.useState<string | null>('');
  const [batchId, setBatchId] = React.useState<any>('');

  const NOT_SET_STR = GetString('NOT_SET_STR');

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  React.useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const getCommandDataRecord = (
    commandData: ScheduledCommandHistoryRecord[]
  ) => {
    let result: ScheduledCommandHistoryRecord[] = [];

    if (Array.isArray(commandData)) {
      commandData.forEach(commandRecord => {
        let data: ScheduledCommandHistoryRecord = {
          ID: commandRecord.ID,
          Uuid: commandRecord.Uuid,
          NodeInfo: commandRecord.NodeInfo,
          Status: commandRecord.Status,
          CreatedOn: commandRecord.CreatedOn,
          StartDate: commandRecord.StartDate,
          EndDate: commandRecord.EndDate,
          CommandBatchID: commandRecord.CommandBatchID,
          Command: commandRecord.Command,
          CronFrequency: commandRecord.CronFrequency,
        };
        result.push(data);
      });
    }

    return result;
  };

  const processWithGroups = (data: any, dataState: State): DataResult => {
    const newDataState = process(data, dataState);

    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };
  const [resultState, setResultState] = React.useState<DataResult>(
    processWithGroups(getCommandDataRecord(scheduledCommandList), dataState)
  );

  const newData = setExpandedState({
    data: resultState.data,
    collapsedIds: collapsedState,
  });

  const extractScheduledLogsParameters = (
    commandListRecord: ScheduledCommandHistoryRecord
  ) => {
    let parameters = commandListRecord.Parameters;
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(parameters ?? '', 'text/xml');

    const FilesElement = xmlDoc.querySelector('Files');
    const filesString = FilesElement ? FilesElement.textContent : '';

    return {
      files: filesString,
    };
  };

  React.useEffect(() => {
    const processedCommandData = getCommandDataRecord(scheduledCommandList);
    setResultState(processWithGroups(processedCommandData, dataState));
    if (scheduledCommandList.length > 0) {
      let extractedParameters = extractScheduledLogsParameters(
        scheduledCommandList[0]
      );
      setStartDate(scheduledCommandList[0].StartDate.split('T')[0]);
      setEndDate(scheduledCommandList[0].EndDate?.split('T')[0]);
      setFrequency(
        cronstrue.toString(scheduledCommandList[0].CronFrequency, {
          locale: currentLocale.locale,
        })
      );
      setFiles(extractedParameters.files);
      setBatchId(scheduledCommandList[0].CommandBatchID);
    }
  }, [scheduledCommandList]);

  const dispatchGetScheduledCommandByBatchId = () => {
    getScheduledCommandByBatchID(dispatch, command, batchId);
  };

  React.useEffect(() => {
    let timeoutId: any;
    if (message !== '') {
      dispatchGetScheduledCommandByBatchId();
      timeoutId = setTimeout(() => {
        resetNotificationMessage(dispatch);
      });
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [message]);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const newDataState = processWithGroups(
      getCommandDataRecord(scheduledCommandList),
      event.dataState
    );
    setResultState(newDataState);
    setDataState(event.dataState);
  };

  const COLUMN_TITLE_STORE = GetString('COLUMN_TITLE_STORE');
  const COLUMN_TITLE_CLUSTER = GetString('COLUMN_TITLE_CLUSTER');
  const COLUMN_TITLE_LANE = GetString('COLUMN_TITLE_LANE');
  const COLUMN_TITLE_DIVISION = GetString('COLUMN_TITLE_DIVISION');
  const COLUMN_TITLE_STATUS = GetString('COLUMN_TITLE_STATUS');
  const COLUMN_TITLE_REQUEST_DATE = GetString('COLUMN_REQUEST_DATE');
  const COLUMN_TITLE_ACTIONS = GetString('COLUMN_TITLE_ACTIONS');
  const CANCEL_REQUEST = GetString('CANCEL_REQUEST');
  const CANCEL_ALL_REQUESTS = GetString('CANCEL_ALL_REQUESTS');

  const requestDateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="CreatedOn"
      />
    );
  };

  const ActionsCell = (props: GridCellProps) => {
    return (
      <td style={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          themeColor="primary"
          onClick={() => {
            updateScheduledCommandById(
              dispatch,
              props.dataItem.ID,
              ApiEnums.ScheduledCommandStatus.Canceled
            );
          }}
          disabled={
            props.dataItem.Status >= ApiEnums.ScheduledCommandStatus.Completed
          }
        >
          {CANCEL_REQUEST}
        </Button>
      </td>
    );
  };

  const StatusCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;

    const status: any = getScheduledCommandStatusToEnum(
      dataItem.Status.toString()
    );

    const commandStatus: any = scheduledCommandStatusTextMap(status);

    let chipStyle: any = useChipStyleScheduledCommand(status);

    if (status) {
      return (
        <td>
          <Chip
            className={chipStyle().root}
            text={GetString(commandStatus)}
            svgIcon={scheduledCommandStatusIconMap(status)}
          />
        </td>
      );
    }
    return null;
  };

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <Dialog
            title={
              <div className={classes.dialogTitle}>
                <StringResourceManager
                  variant="body1"
                  style="commandLabel"
                  stringKey={title}
                  gutterBottom={true}
                />
                <Label className={classes.titleDate}>{creationDate}</Label>
              </div>
            }
            onClose={handleClose}
          >
            <div className={classes.modalContainer}>
              {loading && LoadingPanel}

              {(command === COMMAND_NAME.SCHEDULE_UPLOAD_LOGS ||  command === COMMAND_NAME.SCHEDULE_SET_ALL_WEIGHT_TO_DEFAULT ||  command === COMMAND_NAME.SCHEDULE_CONFIG_ACCESS_CONTROL) && (
                <div className={classes.scheduleUploadLogsContainer}>
                  <div className={classes.queryLabel}>
                    <StringResourceManager
                      variant="body1"
                      style="dialogLabel"
                      stringKey={'START_DATE_RANGE_LABEL'}
                      gutterBottom
                    />
                    <Label className={classes.queryInput}>{startDate}</Label>
                  </div>
                  <div className={classes.queryLabel}>
                    <StringResourceManager
                      variant="body1"
                      style="dialogLabel"
                      stringKey={'END_DATE_RANGE_LABEL'}
                      gutterBottom
                    />
                    <Label className={classes.queryInput}>
                      {endDate ? endDate : NOT_SET_STR}
                    </Label>
                  </div>
                  <div className={classes.queryLabel}>
                    <StringResourceManager
                      variant="body1"
                      style="dialogLabel"
                      stringKey={'FREQUENCY_LABEL'}
                      gutterBottom
                    />
                    <Label className={classes.queryInput}>{frequency}</Label>
                  </div>
                  {command === COMMAND_NAME.SCHEDULE_UPLOAD_LOGS && (
                  <div className={classes.queryLabel}>
                    <StringResourceManager
                      variant="body1"
                      style="dialogLabel"
                      stringKey={'LOG_FILES_STR'}
                      gutterBottom
                    />
                    <Label
                      className={clsx(classes.queryInput, classes.wrapInput)}
                    >
                      {files?.split(';').map((part, index, arr) => (
                        <span key={index}>
                          {part}
                          {index < arr.length - 1 && (
                            <span>
                              ;<wbr />
                            </span>
                          )}
                        </span>
                      ))}
                    </Label>
                  </div>
                  )}
                </div>
              )}
              <Divider />
              {(command === COMMAND_NAME.SCHEDULE_UPLOAD_LOGS ||  command === COMMAND_NAME.SCHEDULE_SET_ALL_WEIGHT_TO_DEFAULT ||  command === COMMAND_NAME.SCHEDULE_CONFIG_ACCESS_CONTROL)  && (
                <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                  <Button
                    themeColor="primary"
                    type="button"
                    onClick={() => {
                      updateScheduledCommandByBatchId(
                        dispatch,
                        batchId,
                        ApiEnums.ScheduledCommandStatus.Canceled
                      );
                    }}
                    style={{ width: '220px' }}
                  >
                    {CANCEL_ALL_REQUESTS}
                  </Button>
                </div>
              )}
              <div>
                <div className={classes.selectedLanesView}>
                  <StringResourceManager
                    variant="body1"
                    style="dialogLabel"
                    stringKey={'SELECTED_LANES'}
                    gutterBottom={true}
                  />
                </div>
                <div>
                  <Grid
                    style={{ height: '300px' }}
                    data={resultState}
                    {...dataState}
                    resizable={true}
                    sortable={true}
                    filterable={true}
                    pageable={{
                      buttonCount: 4,
                      pageSizes: [100, 500, 1000, 5000],
                    }}
                    reorderable={true}
                    fixedScroll={false}
                    onDataStateChange={dataStateChange}
                  >
                    <GridColumn
                      field="NodeInfo.Division"
                      title={COLUMN_TITLE_DIVISION}
                      width="160px"
                    />
                    <GridColumn
                      field="NodeInfo.SiteNumber"
                      title={COLUMN_TITLE_STORE}
                      width="160px"
                    />
                    <GridColumn
                      field="NodeInfo.NodeGroup"
                      title={COLUMN_TITLE_CLUSTER}
                      width="160px"
                    />
                    <GridColumn
                      field="NodeInfo.LaneNumber"
                      title={COLUMN_TITLE_LANE}
                      width="160px"
                    />
                    <GridColumn
                      field="Status"
                      title={COLUMN_TITLE_STATUS}
                      width="200px"
                      cells={{ data: StatusCell, filterCell:ScheduledStatusDropDownFilterCell }}
                    />
                    <GridColumn
                      field="CreatedOn"
                      title={COLUMN_TITLE_REQUEST_DATE}
                      width="200px"
                      cells={{
                        data: CreatedOnCell,
                        filterCell: requestDateFilterCell,
                      }}
                    />
                    {(command === COMMAND_NAME.SCHEDULE_UPLOAD_LOGS ||  command === COMMAND_NAME.SCHEDULE_SET_ALL_WEIGHT_TO_DEFAULT ||  command === COMMAND_NAME.SCHEDULE_CONFIG_ACCESS_CONTROL)  ? (
                      <GridColumn
                        field="Actions"
                        title={COLUMN_TITLE_ACTIONS}
                        width="220px"
                        cells={{
                          data: ActionsCell,
                        }}
                      />
                    ) : null}
                  </Grid>
                </div>
                <div className={classes.doneButtonView}>
                  <Button
                    themeColor={'primary'}
                    type="button"
                    onClick={handleClose}
                  >
                    {GetString('DONE')}
                  </Button>
                </div>
              </div>
            </div>
          </Dialog>
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default ScheduledCommandHistoryDialog;
